import React, { useEffect, useMemo, useRef, useState } from 'react'
import { zynity_api } from '../../../../../config/external-routes'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import Swal from 'sweetalert2'
import { getCnAData } from '../../../../../store/user_organizations/teams/tools/connect_and_align'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import { canDeleteMessage } from '../../../../../helpers/zMail'

export const MemberAccordion = ({ member, closeModal }: any) => {
  const PAGE_SIZE = 20
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.auth)
  const organization_team = useSelector((state: any) => state.organization_team)
  const [showHiddenInfoDiv, setShowHiddenInfoDiv] = useState(false)
  const [showHiddenZMailDiv, setShowHiddenZMailDiv] = useState(false)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)
  const [deletingMessageId, setDeletingMessageId] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [conversationId, setConversationId] = useState<any>(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [nextMessagesCursor, setNextMessagesCursor] = useState<string | null>(null)
  const messageContainerRef = useRef<HTMLDivElement | null>(null)
  const isPrependingOlderMessagesRef = useRef(false)
  const initialBottomScrollDoneRef = useRef(false)

  const scrollToBottom = () => {
    const container = messageContainerRef.current
    if (!container) return false
    if (container.clientHeight <= 0 || container.scrollHeight <= 0) return false
    container.scrollTop = container.scrollHeight
    return true
  }

  const scheduleInitialBottomScroll = (attempt = 0) => {
    if (initialBottomScrollDoneRef.current) return

    requestAnimationFrame(() => {
      const ok = scrollToBottom()
      if (ok) {
        initialBottomScrollDoneRef.current = true
        return
      }

      if (attempt < 10) {
        setTimeout(() => scheduleInitialBottomScroll(attempt + 1), 70)
      }
    })
  }

  const participantId = useMemo(() => {
    if (member?.People_id === undefined || member?.People_id === null) return member?.id ?? null
    return member.People_id
  }, [member])

  const organizationId = organization_team?.data?.idOrganization
  const teamId = organization_team?.data?.id

  const handleInfoDivClick = () => {
    setShowHiddenInfoDiv(!showHiddenInfoDiv)
  }

  const loadConversationMessages = () => {
    if (!participantId || !organizationId || !teamId) return

    const url = zynity_api.user_organization_team_z_mail_conversations
      .replace('{user_id}', user.id)
      .replace('{organization_id}', organizationId)
      .replace('{team_id}', teamId)

    setLoadingMessages(true)
    axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

    const normalizeMessages = (rawMessages: any[]) => {
      return (Array.isArray(rawMessages) ? rawMessages : [])
        .map((item: any) => ({
          id: item?.id ?? item?.message_id,
          body: item?.body ?? item?.message ?? '',
          created_at: item?.created_at ?? item?.dateCreated ?? null,
          sender_id: item?.sender_id ?? item?.from_user_id ?? item?.from_id ?? null,
          sender_name: item?.sender_name ?? item?.from_name ?? 'Unknown',
          can_delete: item?.can_delete,
        }))
        .filter((item: any) => item.body && item.id != null)
    }

    axios
      .get(url, { params: { participant_id: participantId } })
      .then((response) => {
        const responseData = response?.data?.data ?? response?.data ?? []
        const conversations = Array.isArray(responseData) ? responseData : [responseData]

        const firstConversation = conversations.find((c: any) => Array.isArray(c?.messages)) || conversations[0]
        const currentConversationId = firstConversation?.id ?? firstConversation?.conversation_id ?? null
        setConversationId(currentConversationId)

        const rawMessages = Array.isArray(firstConversation?.messages)
          ? firstConversation.messages
          : Array.isArray(firstConversation?.z_mails)
            ? firstConversation.z_mails
            : []
        const normalized = normalizeMessages(rawMessages)
        const hasMore =
          firstConversation?.has_more_messages ??
          firstConversation?.messages_has_more ??
          firstConversation?.has_more ??
          false
        const nextCursor =
          firstConversation?.next_messages_cursor ??
          firstConversation?.messages_next_cursor ??
          firstConversation?.next_cursor ??
          null

        setMessages(normalized)
        setHasMoreMessages(Boolean(hasMore))
        setNextMessagesCursor(nextCursor == null ? null : String(nextCursor))
        scheduleInitialBottomScroll(0)

        if (normalized.length === 0 && currentConversationId) {
          loadOlderMessages(currentConversationId)
        }
      })
      .catch(() => {
        // Keep empty state if backend conversation endpoint is not ready.
        setMessages([])
        setHasMoreMessages(false)
        setNextMessagesCursor(null)
        setConversationId(null)
      })
      .finally(() => {
        setLoadingMessages(false)
      })
  }

  const loadOlderMessages = (conversationIdOverride?: any) => {
    const effectiveConversationId = conversationIdOverride ?? conversationId
    if (!effectiveConversationId || loadingOlderMessages || (!hasMoreMessages && messages.length > 0)) return

    const url = zynity_api.user_organization_team_z_mail_conversation_messages
      .replace('{user_id}', user.id)
      .replace('{organization_id}', organizationId)
      .replace('{team_id}', teamId)
      .replace('{conversation_id}', effectiveConversationId)

    const sortedCurrent = [...messages].sort((a: any, b: any) => {
      const aTs = a?.created_at ? new Date(a.created_at).getTime() : 0
      const bTs = b?.created_at ? new Date(b.created_at).getTime() : 0
      return aTs - bTs
    })
    const oldestMessage = sortedCurrent.length > 0 ? sortedCurrent[0] : null
    const container = messageContainerRef.current
    const previousScrollHeight = container?.scrollHeight ?? 0
    const previousScrollTop = container?.scrollTop ?? 0

    const params: any = {
      limit: PAGE_SIZE,
      ...(nextMessagesCursor ? { cursor: nextMessagesCursor } : {}),
      ...(!nextMessagesCursor && oldestMessage?.id ? { before_message_id: oldestMessage.id } : {}),
      ...(!nextMessagesCursor && !oldestMessage?.id && oldestMessage?.created_at ? { before_created_at: oldestMessage.created_at } : {}),
    }

    setLoadingOlderMessages(true)
    axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

    axios
      .get(url, { params })
      .then((response) => {
        const root = response?.data ?? {}
        const payload = root?.data ?? root
        const rawMessages = Array.isArray(payload) ? payload : payload?.messages ?? payload?.items ?? []
        const normalized = (Array.isArray(rawMessages) ? rawMessages : [])
          .map((item: any) => ({
            id: item?.id ?? item?.message_id,
            body: item?.body ?? item?.message ?? '',
            created_at: item?.created_at ?? item?.dateCreated ?? null,
            sender_id: item?.sender_id ?? item?.from_user_id ?? item?.from_id ?? null,
            sender_name: item?.sender_name ?? item?.from_name ?? 'Unknown',
            can_delete: item?.can_delete,
          }))
          .filter((item: any) => item.body && item.id != null)
        const meta = root?.meta ?? payload?.meta ?? {}
        const apiHasMore = meta?.has_more
        const apiNextCursor = meta?.next_cursor ?? null

        isPrependingOlderMessagesRef.current = true
        setMessages((prev: any[]) => {
          const ids = new Set(prev.map((item: any) => item.id))
          const older = normalized.filter((item: any) => !ids.has(item.id))
          return [...older, ...prev]
        })

        setHasMoreMessages(typeof apiHasMore === 'boolean' ? apiHasMore : normalized.length >= PAGE_SIZE)
        setNextMessagesCursor(apiNextCursor == null ? null : String(apiNextCursor))

        requestAnimationFrame(() => {
          if (!container) return
          const newScrollHeight = container.scrollHeight
          container.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop
          isPrependingOlderMessagesRef.current = false
        })
      })
      .catch(() => {
        setHasMoreMessages(false)
        isPrependingOlderMessagesRef.current = false
      })
      .finally(() => {
        setLoadingOlderMessages(false)
      })
  }

  const handleZMailDivClick = () => {
    const nextOpen = !showHiddenZMailDiv
    setShowHiddenZMailDiv(nextOpen)
    if (nextOpen) {
      initialBottomScrollDoneRef.current = false
      loadConversationMessages()
    }
  }

  const sendZMailHandled = () => {
    if (!message.trim()) return

    const url = zynity_api.user_organization_team_z_mail_post
      .replace('{user_id}', user.id)
      .replace('{organization_id}', organizationId)
      .replace('{team_id}', teamId)

    const payload = {
      assigned_to: participantId,
      message: message.trim(),
      ...(conversationId ? { conversation_id: conversationId } : {}),
    }

    setSending(true)
    axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }
    axios
      .post(`${url}`, payload)
      .then((response) => {
        const responseMessage = response?.data?.message || response?.data?.data || null

        setMessages((prev: any[]) => [
          ...prev,
          {
            id: responseMessage?.id ?? `tmp-${Date.now()}`,
            body: responseMessage?.body ?? responseMessage?.message ?? message.trim(),
            created_at: responseMessage?.created_at ?? new Date().toISOString(),
            sender_id: responseMessage?.sender_id ?? user.id,
            sender_name: responseMessage?.sender_name ?? user?.know_as ?? user?.first_name ?? 'You',
            can_delete: true,
          },
        ])
        setMessage('')
        const responseConversationId = responseMessage?.conversation_id ?? response?.data?.conversation_id ?? null
        if (responseConversationId != null) {
          setConversationId(responseConversationId)
        }
        setTimeout(() => {
          scrollToBottom()
        }, 0)

        dispatch(getCnAData({ organization_id: organizationId, team_id: teamId }))
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Unable to send',
          text: 'Please try again.',
        })
      })
      .finally(() => {
        setSending(false)
      })
  }

  const deleteMessage = (msg: any) => {
    if (!canDeleteMessage(msg, user?.id)) return

    Swal.fire({
      icon: 'warning',
      title: 'Delete message?',
      text: 'This will remove your message for all participants.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (!result.isConfirmed) return

      const newDeleteUrl = zynity_api.user_organization_team_z_mail_message
        .replace('{user_id}', user.id)
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{message_id}', msg.id)

      const legacyDeleteUrl = zynity_api.user_organization_team_z_mail
        .replace('{user_id}', user.id)
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{z_mail_id}', msg.id)

      setDeletingMessageId(msg.id)
      axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

      axios
        .delete(newDeleteUrl)
        .catch(() => axios.delete(legacyDeleteUrl))
        .then(() => {
          setMessages((prev: any[]) => prev.filter((item: any) => item.id !== msg.id))
          setTimeout(() => {
            scrollToBottom()
          }, 0)
          dispatch(getCnAData({ organization_id: organizationId, team_id: teamId }))
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Delete failed',
            text: 'Please try again.',
          })
        })
        .finally(() => {
          setDeletingMessageId(null)
        })
    })
  }

  const sortedMessages = [...messages].sort((a: any, b: any) => {
    const aTs = a?.created_at ? new Date(a.created_at).getTime() : 0
    const bTs = b?.created_at ? new Date(b.created_at).getTime() : 0
    return aTs - bTs
  })

  useEffect(() => {
    if (!showHiddenZMailDiv) return
    if (initialBottomScrollDoneRef.current) return
    if (loadingMessages || loadingOlderMessages) return
    if (sortedMessages.length === 0) return

    scheduleInitialBottomScroll(0)
  }, [showHiddenZMailDiv, sortedMessages.length, loadingMessages, loadingOlderMessages])

  const onMessageScroll = (event: any) => {
    if (!initialBottomScrollDoneRef.current) return
    const target = event?.currentTarget
    if (!target) return
    if (target.scrollTop <= 24 && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages()
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div style={{ color: 'teal', cursor: 'pointer' }} onClick={handleInfoDivClick}>
          {`${member.FirstName} ${member.LastName}`}
          <span style={{ color: '#333' }}>▿</span>
          <img
            style={{ width: '25px' }}
            data-toggle="tooltip"
            className="team-icons mx-2"
            src={require('../../../../../assets/images/FacilitatorIcon.png')}
          ></img>
        </div>
        <div onClick={handleZMailDivClick}>
          <img
            style={{ width: '25px', cursor: 'pointer' }}
            data-toggle="tooltip"
            className="team-icons"
            title="show Z-mail"
            src={require('../../../../../assets/images/ZmailIcon.png')}
            alt="Z-mail"
          ></img>
        </div>
      </div>

      {showHiddenInfoDiv && (
        <div
          style={{
            backgroundColor: 'floralwhite',
            marginLeft: '20px',
            textAlign: 'center',
          }}
        >
          {`${member.EmailAddress} ${member.CellPhone == undefined || member.CellPhone.trim() != '' ? member.Phone : member.CellPhone}`}
        </div>
      )}

      {showHiddenZMailDiv && (
        <div style={{ background: '#f8fffa', border: '1px solid #dcefd6', borderRadius: '8px', padding: '8px', marginTop: '8px' }}>
          <div ref={messageContainerRef} onScroll={onMessageScroll} style={{ maxHeight: '220px', overflowY: 'auto', marginBottom: '8px' }}>
            {loadingMessages && (
              <div className="text-muted" style={{ fontSize: '13px' }}>
                Loading messages...
              </div>
            )}
            {loadingOlderMessages && (
              <div className="text-muted" style={{ fontSize: '12px' }}>
                Loading older messages...
              </div>
            )}

            {!loadingMessages && sortedMessages.length === 0 && (
              <div className="text-muted" style={{ fontSize: '13px' }}>
                No messages yet. Start the chat.
              </div>
            )}

            {!loadingMessages &&
              sortedMessages.map((msg: any) => {
                const ownMessage = Number(msg?.sender_id) === Number(user?.id)
                const canDelete = canDeleteMessage(msg, user?.id)

                return (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: ownMessage ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: '6px', marginBottom: '6px' }}>
                    <div
                      style={{
                        backgroundColor: ownMessage ? '#d9f5ff' : '#ffffff',
                        border: '1px solid #d8e3d4',
                        borderRadius: '10px',
                        padding: '6px 8px',
                        maxWidth: '82%',
                      }}
                    >
                      <div style={{ fontSize: '11px', color: '#607060', marginBottom: '2px' }}>
                        {ownMessage ? 'You' : msg.sender_name}{' '}
                        {msg?.created_at ? `• ${moment(msg.created_at).format('MMM D, h:mm A')}` : ''}
                      </div>
                      <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</div>
                    </div>

                    {canDelete && (
                      <button
                        style={{ background: 'none', border: 'none', color: '#666', padding: '2px' }}
                        disabled={deletingMessageId === msg.id}
                        onClick={() => deleteMessage(msg)}
                        title="Delete message"
                      >
                        {deletingMessageId === msg.id ? <FontAwesomeIcon className="spinner" icon={faSpinner} /> : <FontAwesomeIcon icon={faTrash} />}
                      </button>
                    )}
                  </div>
                )
              })}
          </div>

          <div style={{ textAlign: 'center' }}>
            <textarea
              style={{
                width: '100%',
                border: '1px solid #d0e3cb',
                borderRadius: '6px',
                backgroundColor: '#fff',
                padding: '6px',
              }}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
              }}
              name=""
              id=""
              rows={2}
              placeholder={`Reply to ${member.FirstName}...`}
            ></textarea>
          </div>
          <div className="d-flex flex-row-reverse align-items-center" style={{ gap: '6px' }}>
            <button type="button" className="btn btn-sm btn-teal" disabled={sending || !message.trim()} onClick={sendZMailHandled}>
              {sending ? (
                <>
                  <FontAwesomeIcon className="spinner" icon={faSpinner} /> Sending...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} /> Send
                </>
              )}
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
